import React, { useEffect, useState, useRef } from 'react';
/**
 * LongDivision — Interactive long division (staartdeling) component.
 *
 * Parses dividend ÷ divisor from question text, renders a division bracket
 * layout with answer digit inputs. Supports keyboard navigation.
 *
 * Co-located in practice/ domain (CL-1 s63).
 * Data-first parsing (PEDAGOGICAL_CONTRACT_SPEC §7): prefers question.data.longDivision
 * over regex fallback.
 */
import type { Question } from '../../content/types';
import { warn } from '../../utils/logger';
interface LongDivisionProps {
  question: Question;
  onComplete: (userAnswer: string) => void;
  onReadyChange?: (isReady: boolean) => void;
  shouldValidate?: boolean;
}
/**
 * Data-first division parser (PEDAGOGICAL_CONTRACT_SPEC §7).
 * Prefers structured data from question.data.longDivision,
 * falls back to regex parsing with deprecation warning.
 */
function parseDivision(question: Question): {
  dividend: number;
  divisor: number;
} | null {
  const structured = question.data?.longDivision;
  if (structured && typeof structured.dividend === 'number' && typeof structured.divisor === 'number') {
    return {
      dividend: structured.dividend,
      divisor: structured.divisor
    };
  }
  // Regex fallback — log deprecation warning
  const clean = question.question.replace(/\./g, '').replace(/,/g, '.');
  const match = clean.match(/(\d+)\s*[÷:]\s*(\d+)/);
  if (match) {
    warn(`[LongDivision] Regex fallback for ${question.id} — add data.longDivision to seed`);
    return {
      dividend: parseInt(match[1]),
      divisor: parseInt(match[2])
    };
  }
  return null;
}
const BrokenQuestionState = () => <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-10048">
    <p className="text-red-700 font-bold" data-id="element-10049">⚠️ Vraag kan niet worden geladen</p>
    <p className="text-sm text-red-600 mt-2" data-id="element-10050">
      De deelsom kon niet worden gelezen.
    </p>
  </div>;
export function LongDivision({
  question,
  onComplete,
  onReadyChange,
  shouldValidate = false
}: LongDivisionProps) {
  const [numbers, setNumbers] = useState<number[]>([]);
  const [userInputs, setUserInputs] = useState<string[]>([]);
  const [hasError, setHasError] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevValidateRef = useRef(false);
  // Parse question on mount/change
  useEffect(() => {
    const parsed = parseDivision(question);
    if (parsed) {
      setHasError(false);
      setNumbers([parsed.dividend, parsed.divisor]);
      const resultLen = Math.floor(parsed.dividend / parsed.divisor).toString().length;
      setUserInputs(new Array(resultLen).fill(''));
    } else {
      setHasError(true);
    }
    prevValidateRef.current = false;
  }, [question.id]);
  // Report readiness
  useEffect(() => {
    onReadyChange?.(userInputs.every(v => v !== ''));
  }, [userInputs, onReadyChange]);
  // Reset validation flag
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  // Rising-edge validation
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onComplete(userInputs.join(''));
    }
  }, [shouldValidate, userInputs, onComplete]);
  // Mobile scroll-into-view on focus
  useEffect(() => {
    const handleFocus = () => {
      if (containerRef.current && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        setTimeout(() => containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        }), 300);
      }
    };
    const allInputs = inputRefs.current.filter(Boolean);
    allInputs.forEach(input => input?.addEventListener('focus', handleFocus));
    return () => allInputs.forEach(input => input?.removeEventListener('focus', handleFocus));
  }, [userInputs.length]);
  const handleInputChange = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '');
    const newInputs = [...userInputs];
    newInputs[index] = digit;
    setUserInputs(newInputs);
    if (digit && index < userInputs.length - 1) inputRefs.current[index + 1]?.focus();
  };
  if (hasError) return <BrokenQuestionState data-id="element-10051" />;
  if (numbers.length !== 2) return <div data-id="element-10052">Laden...</div>;
  const [dividend, divisor] = numbers;
  const dividendStr = dividend.toString();
  const divisorStr = divisor.toString();
  return <div ref={containerRef} className="flex flex-col items-center justify-center w-full max-w-md mx-auto" data-id="element-10053">
      <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-indigo-100 w-full" data-id="element-10054">
        <div className="flex justify-between items-center mb-6" data-id="element-10055">
          <h3 className="text-lg font-bold text-slate-700" data-id="element-10056">Staartdeling</h3>
          <div className="text-sm text-slate-500 font-medium bg-slate-100 px-3 py-1 rounded-full" data-id="element-10057">
            Vul de vakjes in
          </div>
        </div>

        <div className="flex justify-center" data-id="element-10058">
          <div className="flex flex-col items-start font-mono text-2xl font-bold text-slate-800" data-id="element-10059">
            {/* Quotient Row */}
            <div className="flex ml-[4.5rem]" data-id="element-10060">
              {userInputs.map((val, i) => <div key={`input-${i}`} className="w-10 h-14 flex items-center justify-center mx-0.5" data-id="element-10061">
                  <input ref={el => inputRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={val} onChange={e => handleInputChange(i, e.target.value)} onKeyDown={e => {
                if (e.key === 'ArrowLeft' && i > 0) inputRefs.current[i - 1]?.focus();
                if (e.key === 'ArrowRight' && i < userInputs.length - 1) inputRefs.current[i + 1]?.focus();
                if (e.key === 'Backspace' && !val && i > 0) inputRefs.current[i - 1]?.focus();
              }} className="w-full h-full text-center text-2xl font-bold rounded-xl border-2 transition-all focus:outline-none focus:ring-4 focus:ring-indigo-100 bg-white border-slate-300 text-slate-900 focus:border-indigo-500" data-id="element-10062" />
                </div>)}
            </div>

            {/* Division Bracket Row */}
            <div className="flex items-center mt-2" data-id="element-10063">
              <div className="w-12 text-right pr-2" data-id="element-10064">{divisorStr}</div>
              <div className="text-slate-400 font-sans px-1" data-id="element-10065">/</div>
              <div className="flex border-t-2 border-l-2 border-slate-800 pl-2 pt-1" data-id="element-10066">
                {dividendStr.split('').map((digit, i) => <div key={`div-${i}`} className="w-10 h-12 flex items-center justify-center mx-0.5" data-id="element-10067">
                    {digit}
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}